import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { ESTIMATION_DETAILS, UPDATE_EXCLUSION_RECALCULATE } from 'graphql/estimationDetails.gql';
import type {
  EstimationLocalData,
  EstimationRowBase,
  UseEstimationDetailsArgs,
  UseEstimationDetailsReturn,
} from '@custom-types/estimation';
import { METERS_REQUIRING_ESTIMATES } from '@graphql/metersRequiringEstimates.gql';
import { ESTIMATION_FACTORS } from '@graphql/estimationFactors.gql';

const getStorageKey = (month_year: string, region: string, energy_type: string) =>
  `estimation_changes_${month_year}_${region}_${energy_type}`;

export default function useEstimationDetails({
  month_year,
  region,
  energy_type,
}: UseEstimationDetailsArgs): UseEstimationDetailsReturn {
  const [localData, setLocalData] = useState<EstimationLocalData>({
    used: [],
    excluded: [],
    estimationFactor: 0,
    isReadOnly: false,
  });
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // NEW: Track if recalculate should be enabled
  const [validationFeedback, setValidationFeedback] = useState<{
    status: 'error' | 'warning' | 'success';
    message: string;
  } | null>(null);

  const userChangesRef = useRef<Record<string, boolean>>({});
  const storageKey = getStorageKey(month_year, region, energy_type);
  const serverEstimationFactorRef = useRef<number>(0); // NEW: Store server's estimation factor

  const { data, loading, error } = useQuery(ESTIMATION_DETAILS, {
    variables: { month_year, region, energy_type },
    skip: !month_year || !region || !energy_type,
    fetchPolicy: 'network-only', // Always get fresh data
    notifyOnNetworkStatusChange: true,
  });

  // In your hook or component that shows the dashboard
  const [updateExclusionAndRecalculate] = useMutation(UPDATE_EXCLUSION_RECALCULATE, {
    refetchQueries: [
      { query: ESTIMATION_DETAILS, variables: { month_year, region, energy_type } },
      { query: ESTIMATION_FACTORS, variables: { month_year } },
      { query: METERS_REQUIRING_ESTIMATES, variables: { month_year } },
    ],
    awaitRefetchQueries: true,
  });

  // Auto-clear validation feedback
  useEffect(() => {
    if (validationFeedback) {
      const timer = setTimeout(() => {
        setValidationFeedback(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [validationFeedback]);

  // Load saved changes from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        userChangesRef.current = JSON.parse(saved);
        setHasUnsavedChanges(Object.keys(userChangesRef.current).length > 0);
      } else {
        userChangesRef.current = {};
        setHasUnsavedChanges(false);
      }
    } catch {
      userChangesRef.current = {};
      setHasUnsavedChanges(false);
    }
  }, [storageKey]);

  const saveChangesToStorage = () => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(userChangesRef.current));
    } catch { }
  };

  // Apply user changes to meter inclusion/exclusion ONLY (not estimation factor)
  const applyUserChangesToData = (serverData: EstimationLocalData): EstimationLocalData => {
    const allMeters = [...serverData.used, ...serverData.excluded];
    const updatedMeters = allMeters.map((meter) => {
      const userChange = userChangesRef.current[meter.meterId];
      return userChange === undefined
        ? meter
        : { ...meter, include: userChange };
    });

    const used = updatedMeters.filter((m) => m.include && m.thisMonth != null);
    const excluded = updatedMeters.filter((m) => !m.include);

    // CRITICAL: Do NOT recalculate estimation factor in FE
    // Keep the server's estimation factor
    return {
      ...serverData,
      used,
      excluded,
      estimationFactor: serverEstimationFactorRef.current // Use server value
    };
  };

  useEffect(() => {
    if (data?.estimationDetails) {
      const serverData = data.estimationDetails as EstimationLocalData;

      // Store the server's estimation factor
      serverEstimationFactorRef.current = serverData.estimationFactor;

      const hasUserChanges = Object.keys(userChangesRef.current).length > 0;
      setLocalData(hasUserChanges ? applyUserChangesToData(serverData) : serverData);
    }
  }, [data]);

  const toggleInclude = (meterId: string) => {
    const allMeters: EstimationRowBase[] = [...localData.used, ...localData.excluded];
    const target = allMeters.find((m) => m.meterId === meterId);
    if (!target) return;

    setValidationFeedback(null);

    //Block approved meters
    if (target.approvalStatus === 'Approved') {
      setValidationFeedback({
        status: 'error',
        message: 'Approved meters cannot be modified. This meter is locked and read-only.',
      });
      return;
    }
    // Restrict "Estimate" read type
    if (target.readType === 'Estimate') {
      setValidationFeedback({
        status: 'error',
        message:
          'Meters with read type "Estimate" cannot be included or excluded from calculations.',
      });
      return;
    }

    // Ensure at least one meter remains checked
    const currentlyIncludedNonApproved = allMeters.filter(
      (m) => m.include && m.approvalStatus !== 'Approved');

    if (currentlyIncludedNonApproved.length === 1 && target.include) {
      setValidationFeedback({
        status: 'error',
        message: 'At least one non-approved meter must remain checked for calculation.',
      });
      return;
    }

    const newIncludeValue = !target.include;
    userChangesRef.current[meterId] = newIncludeValue;
    saveChangesToStorage();
    setHasUnsavedChanges(true); // Enable recalculate button

    const updatedMeters = allMeters.map((m) =>
      m.meterId === meterId ? { ...m, include: newIncludeValue } : m
    );

    const used = updatedMeters.filter((m) => m.include && m.thisMonth != null);
    const excluded = updatedMeters.filter((m) => !m.include);

    // CRITICAL: Do NOT update estimationFactor here
    // Keep the current server estimation factor
    setLocalData({
      ...localData,
      used,
      excluded,
      estimationFactor: serverEstimationFactorRef.current, // Keep server value
    });
  };

  const recalculate = async () => {
    try {
      setIsRecalculating(true);
      setValidationFeedback(null);

      // Only send changes for NON-APPROVED meters that have been modified
      const allMeters: EstimationRowBase[] = [...localData.used, ...localData.excluded];

      const exclusions = Object.entries(userChangesRef.current)
        .filter(([meterId]) => {
          const meter = allMeters.find(m => m.meterId === meterId);
          return meter?.approvalStatus !== 'Approved'; // Skip approved meters
        })
        .map(([meterId, include]) => ({
          meter_id: Number.parseInt(meterId),
          is_excluded: !include,
        }));

      if (exclusions.length === 0) {
        setValidationFeedback({
          status: 'warning',
          message: 'No non-approved meters have changes to recalculate.',
        });
        setIsRecalculating(false);
        return;
      }

      const res = await updateExclusionAndRecalculate({
        variables: { month_year, region, energy_type, exclusions },
      });

      if (res?.data?.updateExclusionAndRecalculate) {
        const serverData = res.data.updateExclusionAndRecalculate as EstimationLocalData;

        serverEstimationFactorRef.current = serverData.estimationFactor;

        // Clear user changes ONLY for non-approved meters
        const approvedMeterIds = allMeters
          .filter(m => m.approvalStatus === 'Approved')
          .map(m => m.meterId);

        approvedMeterIds.forEach(id => {
          delete userChangesRef.current[id];
        });

        sessionStorage.setItem(storageKey, JSON.stringify(userChangesRef.current));
        setHasUnsavedChanges(Object.keys(userChangesRef.current).length > 0);

        setLocalData(serverData);

        setValidationFeedback({
          status: 'success',
          message: 'Recalculation completed successfully. Approved meters remain unchanged.',
        });
      }
    } catch (error: any) {
      setValidationFeedback({
        status: 'error',
        message: error?.message || 'Failed to recalculate. Please try again.',
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  // Clear old cached keys
  useEffect(() => {
    userChangesRef.current = {};
    setValidationFeedback(null);
    setHasUnsavedChanges(false);
    const cleanupTimeout = setTimeout(() => {
      try {
        const currentKey = getStorageKey(month_year, region, energy_type);
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key?.startsWith('estimation_changes_') && key !== currentKey) {
            sessionStorage.removeItem(key);
          }
        }
      } catch { }
    }, 100);
    return () => clearTimeout(cleanupTimeout);
  }, [month_year, region, energy_type]);

  const total: UseEstimationDetailsReturn['total'] = (arr, key) =>
    arr.reduce((sum, item) => sum + (Number.parseFloat(String(item[key])) || 0), 0);

  const isMonthLocked = data?.estimationDetails?.isAnyMeterApprovedForMonth === true;

  return {
    localData,
    toggleInclude,
    recalculate,
    loading,
    error: (error as UseEstimationDetailsReturn['error']) ?? null,
    isRecalculating,
    hasUnsavedChanges, // NEW: Return this flag
    isMonthLocked,
    total,
    validationFeedback,
  };
}